import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Table, Button, Modal, DatePicker, Tag } from 'antd';
import moment from 'moment';
import {
  listCenterOffDates,
  addCenterOffDates,
  removeCenterOffDates,
} from '../../services/api/center-off-dates';
import { showError } from '../../actions';

class NetworkCenterOffDates extends Component {
  constructor(props) {
    super(props);
    this.state = {
      offDates: [],
      loading: false,
      modalVisible: false,
      pendingDates: [],       // YYYYMMDD strings staged in modal
      selectedDate: null,     // current DatePicker moment value
      submitting: false,
      removing: false,
    };
  }

  componentDidMount() {
    this.fetchOffDates();
  }

  fetchOffDates = async () => {
    if (!this.props.centerId) return;
    this.setState({ loading: true });
    try {
      const res = await listCenterOffDates(this.props.centerId);
      this.setState({ offDates: res.body.offDates || [] });
    } catch (err) {
      this.props.showError('Failed to load off-dates', 'Please try again.');
    } finally {
      this.setState({ loading: false });
    }
  };

  openModal = () => {
    this.setState({ modalVisible: true, pendingDates: [], selectedDate: null });
  };

  closeModal = () => {
    this.setState({ modalVisible: false, pendingDates: [], selectedDate: null });
  };

  addToList = () => {
    const { selectedDate } = this.state;
    if (!selectedDate) return;
    const datekey = selectedDate.format('YYYYMMDD');

    this.setState((prev) => {
      const alreadyPending = prev.pendingDates.includes(datekey);
      const alreadyAdded = prev.offDates.some((d) => d.datekey === datekey);
      if (alreadyPending || alreadyAdded) {
        return { selectedDate: null };
      }
      return { pendingDates: [...prev.pendingDates, datekey], selectedDate: null };
    });
  };

  removePending = (datekey) => {
    this.setState((prev) => ({
      pendingDates: prev.pendingDates.filter((d) => d !== datekey),
    }));
  };

  confirmAdd = async () => {
    const { pendingDates } = this.state;
    if (!pendingDates.length) return;

    this.setState({ submitting: true });
    try {
      await addCenterOffDates(this.props.centerId, pendingDates);
      await this.fetchOffDates();
      this.closeModal();
    } catch (err) {
      this.props.showError('Failed to add off-dates', 'Please try again.');
    } finally {
      this.setState({ submitting: false });
    }
  };

  removeOffDate = (datekey) => {
    if (this.state.removing) return;
    Modal.confirm({
      title: 'Remove off-date?',
      content: `This will re-enable OPD bookings on ${moment(datekey, 'YYYYMMDD').format('DD MMM YYYY')}.`,
      okText: 'Remove',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        this.setState({ removing: true });
        try {
          await removeCenterOffDates(this.props.centerId, [datekey]);
          await this.fetchOffDates();
        } catch (err) {
          this.props.showError('Failed to remove off-date', 'Please try again.');
        } finally {
          this.setState({ removing: false });
        }
      },
    });
  };

  isDisabledDate = (current) => {
    if (!current) return false;
    const isPast = current.isBefore(moment().startOf('day'));
    const datekey = current.format('YYYYMMDD');
    const alreadyAdded = this.state.offDates.some((d) => d.datekey === datekey);
    const alreadyPending = this.state.pendingDates.includes(datekey);
    return isPast || alreadyAdded || alreadyPending;
  };

  getColumns = () => [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (date) => moment(date, 'YYYY-MM-DD').format('DD MMM YYYY'),
    },
    {
      title: 'Day',
      dataIndex: 'datekey',
      key: 'day',
      render: (datekey) => moment(datekey, 'YYYYMMDD').format('dddd'),
    },
    {
      title: 'Added On',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (val) => moment(val).format('DD MMM YYYY'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button
          type="danger"
          size="small"
          disabled={this.state.removing}
          onClick={() => this.removeOffDate(record.datekey)}
        >
          Remove
        </Button>
      ),
    },
  ];

  render() {
    const {
      offDates, loading, modalVisible,
      pendingDates, selectedDate, submitting,
    } = this.state;

    return (
      <div>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>OPD Off-Dates</h3>
          <Button type="primary" onClick={this.openModal}>
            Add Off-Dates
          </Button>
        </div>

        <Table
          dataSource={offDates}
          columns={this.getColumns()}
          rowKey="datekey"
          loading={loading}
          locale={{ emptyText: 'No off-dates configured for this center.' }}
          pagination={{ pageSize: 20 }}
        />

        <Modal
          title="Add Off-Dates"
          visible={modalVisible}
          onCancel={this.closeModal}
          footer={[
            <Button key="cancel" onClick={this.closeModal}>
              Cancel
            </Button>,
            <Button
              key="confirm"
              type="primary"
              disabled={!pendingDates.length}
              loading={submitting}
              onClick={this.confirmAdd}
            >
              Confirm ({pendingDates.length})
            </Button>,
          ]}
        >
          <div style={{ marginBottom: 12 }}>
            <DatePicker
              value={selectedDate}
              onChange={(date) => this.setState({ selectedDate: date })}
              disabledDate={this.isDisabledDate}
              style={{ marginRight: 8 }}
              format="DD MMM YYYY"
            />
            <Button onClick={this.addToList} disabled={!selectedDate}>
              Add to List
            </Button>
          </div>

          <div>
            {pendingDates.length === 0 && (
              <p style={{ color: '#aaa' }}>No dates selected yet.</p>
            )}
            {pendingDates.map((dk) => (
              <Tag
                key={dk}
                closable
                onClose={() => this.removePending(dk)}
                style={{ marginBottom: 6 }}
              >
                {moment(dk, 'YYYYMMDD').format('DD MMM YYYY')}
              </Tag>
            ))}
          </div>
        </Modal>
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch) => ({
  showError: (title, desc) => dispatch(showError(title, desc)),
});

export default connect(null, mapDispatchToProps)(NetworkCenterOffDates);
